import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useLocale, useTranslation, SUPPORTED_LOCALES } from "./contexts/LocaleContext";
import { trackEvent } from "./AppRouter";
import { LandingNav } from "./LandingPage";
import SEO from "./components/SEO";
import {
  CheckCircle,
  ArrowLeft,
  BarChart3,
  Globe,
  Zap,
  Shield,
  Wallet,
  CreditCard,
  Clock,
  ChevronRight,
  Calendar,
  FileText,
  HelpCircle,
  AlertTriangle,
  Info,
  ChevronDown,
} from "lucide-react";

export default function Blog() {
  const t = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const { postSlug } = useParams();

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("theme");
    return saved === "dark" || (!saved && window.matchMedia("(prefers-color-scheme: dark)").matches);
  });

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          setIsDarkMode(document.documentElement.classList.contains("dark"));
        }
      });
    });
    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  const posts = [
    {
      id: "how-to-issue-invoice-sole-proprietor-czech-republic",
      slug: "how-to-issue-invoice-sole-proprietor-czech-republic",
      date: "2026-01-28",
      readTime: "6 min",
      content: t.blog.post2,
      icon: <FileText className="text-pink-400" />,
      type: "guide",
    },
    {
      id: "comparison-of-invoicing-tools-czech-republic",
      slug: "comparison-of-invoicing-tools-czech-republic",
      date: "2026-01-03",
      readTime: "5 min",
      content: t.blog.post1,
      icon: <BarChart3 className="text-purple-400" />,
      type: "comparison",
    },
  ];

  const currentPost = posts.find((p) => p.slug === postSlug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPost]);

  const handlePostClick = (slug: string) => {
    const path = location.pathname.endsWith("/") 
      ? `${location.pathname}${slug}` 
      : `${location.pathname}/${slug}`;
    navigate(path);
  };

  const handleBack = () => {
    if (postSlug) {
      // Remove the slug from the end of the path
      const newPath = location.pathname.split("/").slice(0, -1).join("/") || "/";
      navigate(newPath);
    } else {
      // Navigate to home in current locale
      const pathParts = location.pathname.split("/");
      const isLocalePath = SUPPORTED_LOCALES.includes(pathParts[1] as any);
      const targetLocale = isLocalePath ? pathParts[1] : "cs";
      const newPath = targetLocale === "cs" ? "/" : `/${targetLocale}`;
      navigate(newPath);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white selection:bg-purple-500/30 transition-colors duration-300">
      <SEO 
        title={currentPost ? `${currentPost.content.title} - Invoitio Blog` : `${t.blog.title} - Invoitio`}
        description={currentPost ? currentPost.content.intro : (locale === 'ru' ? "Блог Invoitio: все о создании счетов, налогах и финансах в Чехии." : locale === 'cs' ? "Invoitio Blog: vše o fakturaci, daních a financích v ČR." : "Invoitio Blog: everything about invoicing, taxes and finance in the Czech Republic.")}
      />

      {currentPost && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": currentPost.content.title,
            "description": currentPost.content.intro,
            "datePublished": currentPost.date,
            "author": {
              "@type": "Organization",
              "name": "Invoitio"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Invoitio",
              "logo": {
                "@type": "ImageObject",
                "url": "https://invoitio.com/logo512.png"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://invoitio.com${locale === 'cs' ? '' : '/' + locale}/blog/${currentPost.slug}`
            }
          })}
        </script>
      )}

      {/* Background Decor */}
      <div className="fixed inset-0 bg-white dark:bg-slate-950 -z-20 transition-colors duration-300" />
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_top_right,_#e0e7ff_0%,_transparent_50%)] dark:bg-[radial-gradient(circle_at_top_right,_#1e1b4b_0%,_transparent_50%)] pointer-events-none -z-10 transition-colors duration-300" />
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_bottom_left,_#e0e7ff_0%,_transparent_50%)] dark:bg-[radial-gradient(circle_at_bottom_left,_#1e1b4b_0%,_transparent_50%)] pointer-events-none -z-10 transition-colors duration-300" />

      <LandingNav />

      <main className="relative mx-auto max-w-5xl px-4 py-12 md:py-20">
        <AnimatePresence mode="wait">
          {!postSlug ? (
            <motion.div
              key="feed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              {/* Feed Header */}
              <div className="mb-16 text-center">
                <h1 className="text-5xl md:text-7xl font-black mb-6 bg-gradient-to-r from-slate-900 via-slate-800 to-purple-600 dark:from-white dark:via-white dark:to-purple-400 bg-clip-text text-transparent tracking-tight">
                  {t.blog.title}
                </h1>
                <p className="text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto leading-relaxed">
                  {locale === "ru"
                    ? "Полезные статьи об инвойсинге, налогах и управлении финансами для фрилансеров и бизнеса."
                    : locale === "cs"
                    ? "Užitečné články o fakturaci, daních a finančním řízení pro freelancery a firmy."
                    : "Useful articles about invoicing, taxes, and financial management for freelancers and businesses."}
                </p>
              </div>

              {/* Posts Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {posts.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    onClick={() => handlePostClick(post.slug)}
                  />
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="detail"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              {currentPost ? (
                <PostDetail
                  post={currentPost}
                  onBack={handleBack}
                  t={t}
                  locale={locale}
                  navigate={navigate}
                />
              ) : (
                <div className="text-center py-20">
                  <h2 className="text-3xl font-bold mb-4">Post not found</h2>
                  <button
                    onClick={handleBack}
                    className="text-purple-600 dark:text-purple-400 hover:underline bg-transparent border-0 cursor-pointer"
                  >
                    Back to Blog
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}

function PostCard({ post, onClick }: { post: any; onClick: () => void }) {
  return (
    <motion.div
      whileHover={{ y: -8 }}
      onClick={onClick}
      className="group cursor-pointer relative overflow-hidden rounded-[2.5rem] border border-slate-200 dark:border-purple-900/30 bg-white/50 dark:bg-white/5 backdrop-blur-sm transition-all hover:bg-white/80 dark:hover:bg-white/10 hover:border-purple-300 dark:hover:border-purple-500/50 shadow-sm hover:shadow-xl"
    >
      <div className="p-8 md:p-10 flex flex-col h-full">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
            {post.icon}
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-3 text-xs font-bold text-purple-600 dark:text-purple-400 uppercase tracking-widest">
              <span className="flex items-center gap-1">
                <Calendar size={12} />
                {new Date(post.date).toLocaleDateString()}
              </span>
              <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-700" />
              <span className="flex items-center gap-1">
                <Clock size={12} />
                {post.readTime}
              </span>
            </div>
          </div>
        </div>

        <h3 className="text-2xl md:text-3xl font-bold mb-4 leading-tight text-slate-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-300 transition-colors">
          {post.content.title}
        </h3>

        <p className="text-slate-600 dark:text-slate-400 line-clamp-3 mb-8 leading-relaxed">
          {post.content.intro}
        </p>

        <div className="mt-auto flex items-center gap-2 text-purple-600 dark:text-purple-400 font-bold group-hover:gap-3 transition-all">
          <span>Read more</span>
          <ChevronRight size={20} />
        </div>
      </div>
    </motion.div>
  );
}

function PostDetail({ post, onBack, t, locale, navigate }: any) {
  const content = post.content;

  if (post.type === "guide") {
    return <GuidePost post={post} onBack={onBack} t={t} locale={locale} navigate={navigate} />;
  }

  return (
    <article className="max-w-4xl mx-auto">
      {/* Back Button */}
      <motion.button
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={onBack}
        className="flex items-center gap-2 text-purple-600 dark:text-purple-400 hover:text-purple-500 dark:hover:text-purple-300 transition-colors mb-12 bg-transparent border-0 cursor-pointer group"
      >
        <ArrowLeft
          size={18}
          className="group-hover:-translate-x-1 transition-transform"
        />
        <span className="font-bold uppercase tracking-widest text-xs">
          Back to feed
        </span>
      </motion.button>

      {/* Post Header */}
      <header className="mb-16">
        <div className="flex items-center gap-4 mb-8">
          <div className="flex items-center gap-2 text-sm font-bold text-slate-500 uppercase tracking-widest">
            <Calendar size={14} />
            {new Date(post.date).toLocaleDateString()}
          </div>
          <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-700" />
          <div className="flex items-center gap-2 text-sm font-bold text-slate-500 uppercase tracking-widest">
            <Clock size={14} />
            {post.readTime}
          </div>
        </div>

        <h1 className="text-4xl md:text-6xl font-black mb-10 leading-[1.1] tracking-tight text-slate-900 dark:text-white">
          {content.title}
        </h1>

        <div className="relative p-8 md:p-10 rounded-[2.5rem] bg-purple-50 dark:bg-purple-900/10 border-l-8 border-purple-600">
          <p className="text-xl md:text-2xl text-slate-700 dark:text-slate-300 leading-relaxed font-medium italic">
            {content.intro}
          </p>
        </div>
      </header>

      {/* Comparison Table */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mb-20 overflow-hidden rounded-[2.5rem] border border-slate-200 dark:border-purple-900/30 bg-white/50 dark:bg-white/5 backdrop-blur-sm shadow-xl dark:shadow-2xl"
      >
        <div className="p-8 border-b border-slate-200 dark:border-purple-900/30 bg-slate-50 dark:bg-purple-900/10 flex items-center justify-between">
          <h2 className="text-2xl font-bold flex items-center gap-3 text-slate-900 dark:text-white">
            <BarChart3 className="text-purple-600 dark:text-purple-400" />
            {content.comparisonTitle}
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 dark:bg-white/5 text-purple-700 dark:text-purple-200 uppercase text-[10px] tracking-[0.2em] font-black">
                <th className="p-6 border-b border-slate-200 dark:border-purple-900/20">
                  {content.table.feature}
                </th>
                <th className="p-6 border-b border-slate-200 dark:border-purple-900/20 text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-400/5">
                  Invoitio
                </th>
                <th className="p-6 border-b border-slate-200 dark:border-purple-900/20">Fakturoid</th>
                <th className="p-6 border-b border-slate-200 dark:border-purple-900/20">iDoklad</th>
                <th className="p-6 border-b border-slate-200 dark:border-purple-900/20">
                  FakturyWeb
                </th>
              </tr>
            </thead>
            <tbody className="text-sm text-slate-600 dark:text-slate-300">
              <TableRow
                label={content.table.languages}
                v1="RU, EN, DE, CS, SK"
                v2="CS"
                v3="CS"
                v4="CS"
                highlight
              />
              <TableRow
                label={content.table.currencies}
                v1={content.table.yes}
                v2={content.table.limited}
                v3={content.table.limited}
                v4="CZ / EUR"
                highlight
              />
              <TableRow
                label={content.table.rates}
                v1={content.table.yes}
                v2={content.table.no}
                v3={content.table.yes}
                v4={content.table.no}
                highlight
              />
              <TableRow
                label={content.table.analytics}
                v1={content.table.yes}
                v2={content.table.no}
                v3={content.table.partial}
                v4={content.table.no}
                highlight
              />
              <TableRow
                label={content.table.multiProfit}
                v1={content.table.yes}
                v2={content.table.no}
                v3={content.table.no}
                v4={content.table.no}
                highlight
              />
              <TableRow
                label={content.table.accountingAccess}
                v1={content.table.yes}
                v2={content.table.yes}
                v3={content.table.yes}
                v4={content.table.no}
                highlight
              />
              <TableRow
                label={content.table.lifetime}
                v1={content.table.yes}
                v2={content.table.no}
                v3={content.table.no}
                v4={content.table.no}
                highlight
              />
              <TableRow
                label={content.table.speed}
                v1={content.table.seconds}
                v2={content.table.faster}
                v3={content.table.faster}
                v4={content.table.average}
                highlight
                isLast
              />
            </tbody>
          </table>
        </div>
      </motion.section>

      {/* Main Content Blocks */}
      <div className="space-y-24 mb-24">
        <section>
          <h2 className="text-3xl font-black mb-8 text-slate-900 dark:text-white">{content.focusTitle}</h2>
          <p className="text-lg text-slate-700 dark:text-slate-300 leading-relaxed">
            {content.focusText}
          </p>
        </section>

        <section>
          <h2 className="text-3xl font-black mb-12 text-center text-slate-900 dark:text-white">
            {content.featuresTitle}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <FeatureItem
              icon={<Globe className="text-blue-600 dark:text-blue-400" />}
              title={content.feature1Title}
              text={content.feature1Text}
            />
            <FeatureItem
              icon={<Zap className="text-yellow-600 dark:text-yellow-400" />}
              title={content.feature2Title}
              text={content.feature2Text}
            />
            <FeatureItem
              icon={<Wallet className="text-emerald-600 dark:text-emerald-400" />}
              title={content.feature3Title}
              text={content.feature3Text}
            />
            <FeatureItem
              icon={<BarChart3 className="text-purple-600 dark:text-purple-400" />}
              title={content.feature4Title}
              text={content.feature4Text}
            />
            <FeatureItem
              icon={<Shield className="text-indigo-600 dark:text-indigo-400" />}
              title={content.feature5Title}
              text={content.feature5Text}
            />
            <FeatureItem
              icon={<CreditCard className="text-pink-600 dark:text-pink-400" />}
              title={content.feature6Title}
              text={content.feature6Text}
            />
          </div>
        </section>

        {/* Competitors Detailed */}
        <section className="space-y-12">
          <CompetitorBlock
            title={content.fakturoidTitle}
            text={content.fakturoidText}
            borderColor="border-orange-500/30"
            bgColor="bg-orange-500/5"
          />
          <CompetitorBlock
            title={content.idokladTitle}
            text={content.idokladText}
            borderColor="border-blue-500/30"
            bgColor="bg-blue-500/5"
          />
          <CompetitorBlock
            title={content.fakturywebTitle}
            text={content.fakturywebText}
            borderColor="border-slate-500/30"
            bgColor="bg-slate-500/5"
          />
        </section>

        {/* Final CTA */}
        <section>
          <h2 className="text-3xl font-black mb-8 text-slate-900 dark:text-white">{content.conclusionTitle}</h2>
          <p className="text-lg text-slate-700 dark:text-slate-300 leading-relaxed mb-12">
            {content.conclusionText}
          </p>

          <motion.div
            whileHover={{ scale: 1.01 }}
            className="rounded-[3rem] bg-gradient-to-br from-pink-600 via-purple-600 to-indigo-700 p-10 md:p-16 text-center shadow-2xl shadow-purple-500/30"
          >
            <h2 className="text-3xl md:text-5xl font-black mb-8 text-white">
              {t.landing.cta.title}
            </h2>
            <p className="text-xl text-purple-100 mb-12 max-w-2xl mx-auto font-medium">
              {t.landing.cta.subtitle}
            </p>
            <button
              onClick={() => {
                trackEvent("signup_click", { location: "blog_cta" });
                navigate(locale === 'cs' ? "/signup" : `/${locale}/signup`);
              }}
              className="px-12 py-5 rounded-2xl bg-white text-purple-900 font-black text-xl hover:bg-purple-50 transition-all shadow-2xl hover:-translate-y-1 border-0 cursor-pointer"
            >
              {t.landing.hero.tryButton}
            </button>
          </motion.div>
        </section>
      </div>
    </article>
  );
}

function TableRow({ label, v1, v2, v3, v4, highlight, isLast }: any) {
  return (
    <tr
      className={`${
        !isLast ? "border-b border-slate-200 dark:border-purple-900/10" : ""
      } hover:bg-slate-50 dark:hover:bg-white/5 transition-colors`}
    >
      <td className="p-6 font-bold text-slate-400 uppercase text-[10px] tracking-widest">
        {label}
      </td>
      <td className="p-6 text-emerald-600 dark:text-emerald-400 font-black bg-purple-50 dark:bg-purple-400/5">{v1}</td>
      <td className="p-6 font-medium text-slate-500 dark:text-slate-400">{v2}</td>
      <td className="p-6 font-medium text-slate-500 dark:text-slate-400">{v3}</td>
      <td className="p-6 font-medium text-slate-500 dark:text-slate-400">{v4}</td>
    </tr>
  );
}

function FeatureItem({ icon, title, text }: any) {
  return (
    <div className="p-8 rounded-[2rem] border border-slate-200 dark:border-white/10 bg-white/50 dark:bg-white/5 backdrop-blur-sm shadow-sm">
      <div className="w-14 h-14 rounded-2xl bg-slate-100 dark:bg-white/5 flex items-center justify-center mb-6 border border-slate-200 dark:border-white/10">
        {icon}
      </div>
      <h3 className="text-xl font-bold mb-4 text-slate-900 dark:text-white">{title}</h3>
      <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm">{text}</p>
    </div>
  );
}

function CompetitorBlock({ title, text, borderColor, bgColor }: any) {
  return (
    <div className={`p-10 rounded-[2.5rem] border ${borderColor} ${bgColor} backdrop-blur-sm shadow-sm`}>
      <h3 className="text-2xl font-black mb-6 text-slate-900 dark:text-white">{title}</h3>
      <p className="text-lg text-slate-700 dark:text-slate-300 leading-relaxed">{text}</p>
    </div>
  );
}

function GuidePost({ post, onBack, t, locale, navigate }: any) {
  const content = post.content;
  const [isVatOpen, setIsVatOpen] = useState(false);

  return (
    <article className="max-w-4xl mx-auto">
      {/* Back Button */}
      <motion.button
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={onBack}
        className="flex items-center gap-2 text-purple-600 dark:text-purple-400 hover:text-purple-500 dark:hover:text-purple-300 transition-colors mb-12 bg-transparent border-0 cursor-pointer group"
      >
        <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
        <span className="font-bold uppercase tracking-widest text-xs">Back to feed</span>
      </motion.button>

      {/* Post Header */}
      <header className="mb-16">
        <div className="flex items-center gap-4 mb-8">
          <div className="flex items-center gap-2 text-sm font-bold text-slate-500 uppercase tracking-widest">
            <Calendar size={14} />
            {new Date(post.date).toLocaleDateString()}
          </div>
          <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-700" />
          <div className="flex items-center gap-2 text-sm font-bold text-slate-500 uppercase tracking-widest">
            <Clock size={14} />
            {post.readTime}
          </div>
        </div>

        <h1 className="text-4xl md:text-6xl font-black mb-10 leading-[1.1] tracking-tight text-slate-900 dark:text-white">
          {content.title}
        </h1>
      </header>

      {/* Quick CTA */}
      <div className="mb-20 py-12 border-y border-slate-100 dark:border-white/5">
        <div className="flex flex-col items-center text-center max-w-2xl mx-auto">
          <h3 className="text-3xl md:text-4xl font-black mb-6 text-slate-900 dark:text-white uppercase tracking-tight">
            {content.ctaTitle}
          </h3>
          <p className="text-lg md:text-xl text-slate-600 dark:text-slate-400 font-medium leading-relaxed mb-10">
            {content.ctaText}
          </p>
          <button
            onClick={() => navigate(locale === 'cs' ? "/signup" : `/${locale}/signup`)}
            className="px-10 py-4 rounded-2xl bg-[#E00B4B] text-white font-black text-xl hover:shadow-xl hover:shadow-pink-500/30 transition-all border-0 cursor-pointer whitespace-nowrap active:scale-95"
          >
            {t.landing.nav.tryIt}
          </button>
        </div>
      </div>

      {/* Content Sections */}
      <div className="space-y-20 mb-24">
        {/* Q1 */}
        <section>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center border border-pink-200 dark:border-pink-800/50">
              <HelpCircle className="text-pink-600 dark:text-pink-400" />
            </div>
            <h2 className="text-3xl font-black text-slate-900 dark:text-white">{content.q1Title}</h2>
          </div>
          <p className="text-lg text-slate-700 dark:text-slate-300 leading-relaxed">
            {content.q1Text}
          </p>
        </section>

        {/* Q2 - VAT Section with Accordion */}
        <section className="p-8 md:p-10 rounded-[2.5rem] border-2 border-slate-200 dark:border-white/10 bg-white/50 dark:bg-white/5 backdrop-blur-sm">
          <button 
            onClick={() => setIsVatOpen(!isVatOpen)}
            className="w-full flex items-center justify-between text-left bg-transparent border-0 cursor-pointer group"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center border border-indigo-200 dark:border-indigo-800/50">
                <Info className="text-indigo-600 dark:text-indigo-400" />
              </div>
              <h2 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                {content.q2Title}
              </h2>
            </div>
            <ChevronDown 
              className={`text-slate-400 transition-transform duration-300 ${isVatOpen ? 'rotate-180' : ''}`} 
              size={32} 
            />
          </button>
          
          <AnimatePresence>
            {isVatOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="pt-8 space-y-6">
                  <p className="text-lg text-slate-700 dark:text-slate-300 font-bold">
                    {content.q2Text}
                  </p>
                  <ul className="space-y-4">
                    {content.q2List.map((item: string, i: number) => (
                      <li key={i} className="flex items-start gap-3 text-slate-600 dark:text-slate-400">
                        <CheckCircle className="text-emerald-500 mt-1 flex-shrink-0" size={20} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="p-6 rounded-2xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/50 flex items-start gap-4">
                    <AlertTriangle className="text-amber-600 dark:text-amber-400 flex-shrink-0" size={24} />
                    <p className="text-amber-800 dark:text-amber-200 font-medium">
                      {content.q2Note}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </section>

        {/* Section 1 - Mandatory Data */}
        <section>
          <h2 className="text-3xl font-black mb-8 text-slate-900 dark:text-white">{content.section1Title}</h2>
          <p className="text-lg text-slate-700 dark:text-slate-300 leading-relaxed mb-8">
            {content.section1Text}
          </p>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-8 rounded-[2rem] bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10">
              <h3 className="text-xl font-bold mb-6 text-pink-600 dark:text-pink-400 uppercase tracking-wider">
                {content.section1MandatoryTitle}
              </h3>
              <ul className="space-y-4">
                {content.section1MandatoryList.map((item: string, i: number) => (
                  <li key={i} className="flex items-start gap-3 text-slate-600 dark:text-slate-400">
                    <div className="w-1.5 h-1.5 rounded-full bg-pink-500 mt-2.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm italic text-slate-500 dark:text-slate-400">
                {content.section1MandatoryNote}
              </p>
            </div>

            <div className="p-8 rounded-[2rem] bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10">
              <h3 className="text-xl font-bold mb-6 text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">
                {content.section1OptionalTitle}
              </h3>
              <ul className="space-y-4">
                {content.section1OptionalList.map((item: string, i: number) => (
                  <li key={i} className="flex items-start gap-3 text-slate-600 dark:text-slate-400">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Q3 - Due Date */}
        <section className="p-8 md:p-10 rounded-[2.5rem] bg-emerald-50 dark:bg-emerald-900/10 border-2 border-emerald-200 dark:border-emerald-800/50">
          <h2 className="text-2xl font-black mb-4 text-emerald-900 dark:text-emerald-100">{content.q3Title}</h2>
          <p className="text-lg text-emerald-800 dark:text-emerald-200 leading-relaxed">
            {content.q3Text}
          </p>
        </section>

        {/* Edge Case */}
        <section>
          <div className="flex items-center gap-4 mb-8">
            <div className="px-4 py-1 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-xs font-black uppercase tracking-widest">
              Edge Case
            </div>
            <h2 className="text-3xl font-black text-slate-900 dark:text-white">{content.edgeCaseTitle}</h2>
          </div>
          <p className="text-lg text-slate-700 dark:text-slate-300 leading-relaxed mb-10">
            {content.edgeCaseText}
          </p>
          
          <div className="p-8 md:p-10 rounded-[2.5rem] bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-white/10 shadow-xl">
            <h3 className="text-xl font-bold mb-8 text-slate-900 dark:text-white leading-relaxed">
              {content.edgeCaseMandatoryTitle}
            </h3>
            <div className="grid sm:grid-cols-2 gap-6">
              {content.edgeCaseMandatoryList.map((item: string, i: number) => (
                <div key={i} className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/5">
                  <CheckCircle className="text-emerald-500 flex-shrink-0" size={24} />
                  <span className="text-slate-700 dark:text-slate-300 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <motion.div
          whileHover={{ scale: 1.01 }}
          className="rounded-[3rem] bg-gradient-to-br from-pink-600 via-purple-600 to-indigo-700 p-10 md:p-16 text-center shadow-2xl shadow-purple-500/30"
        >
          <p className="text-2xl md:text-3xl text-white mb-12 max-w-3xl mx-auto font-black leading-tight">
            {content.finalCtaText}
          </p>
          <button
            onClick={() => {
              trackEvent("signup_click", { location: "blog_guide_cta" });
              navigate(locale === 'cs' ? "/signup" : `/${locale}/signup`);
            }}
            className="px-12 py-5 rounded-2xl bg-white text-purple-900 font-black text-xl hover:bg-purple-50 transition-all shadow-2xl hover:-translate-y-1 border-0 cursor-pointer"
          >
            {t.landing.nav.start}
          </button>
        </motion.div>
      </div>
    </article>
  );
}
